import { IBreadcrumbProps, Text } from "@fluentui/react";
import { New16Regular } from "@fluentui/react-icons";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CustomBreadcrumb } from "../SharedComponents/CustomBreadcrumb";
import FeatureDetailsCard from "./FeatureDetailsCard";
import StageFlow from "./StageFlow";
import {
  useLazyGetFeatureRequestFlowQuery,
  useLazyGetFeatureDetailsByFeatureIdQuery
} from "../slices/ExploreFeatureRequests.slice";
import { logPageView } from "../helper/AppInsightsHelper";
import DisplayAPIsException from "../BasicComponents/DisplayAPIsExceptionAlert";
import WarningBanner from "../helper/WarningBanner";

const breadcrumbStyles = {
  root: {
    fontSize: "14px",
    margin: 0,
    "--itemLink-color": "rgb(0, 120, 212)"
  },
  item: {
    fontSize: "inherit"
  },
  itemLink: {
    fontSize: "inherit",
    color: "var(--itemLink-color)",
    ":hover": {
      background: "none",
      textDecoration: "underline",
      color: "var(--itemLink-color)"
    }
  }
};
const key = "feature-request-flow-page-bc";

const FeatureFlowPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const featureRequestId = searchParams.get("id");
  const [flowData, setFlowData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<any>(location.state?.featureData || null);
  const hasLoggedRef = React.useRef(false);
  const styles = {
    featureFlowCard: { marginRight: 10 } as React.CSSProperties,
    featureFlowHeader: {
      flexGrow: 1,
      flexDirection: "row",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    } as React.CSSProperties,
    betaBadge: {
      marginTop: "10px",
      marginRight: "16px",
      marginBottom: "10px",
      backgroundColor: "#0078D4",
      color: "#FFFFFF",
      padding: "4px 12px",
      borderRadius: "9999px",
      display: "inline-flex",
      alignItems: "center",
      fontSize: "12px",
      fontWeight: 500
    } as React.CSSProperties
  };

  const bcrops: IBreadcrumbProps = {
    items: [
      { text: "Home", key: "home", onClick: () => navigate("/") },
      {
        text: "Explore Feature Requests",
        key: "explore-feature-requests",
        onClick: () => navigate("/featurerequestsearch")
      },
      { text: "Feature Request Flow", key: "feature-request-flow", isCurrentItem: true }
    ],
    styles: breadcrumbStyles
  };
  const [triggerFlow, { error, isError, isLoading }] = useLazyGetFeatureRequestFlowQuery();
  const [triggerFeatureDetails] = useLazyGetFeatureDetailsByFeatureIdQuery();

  useEffect(() => {
    if (!featureRequestId?.trim()) return;

    const loadData = async () => {
      setLoading(true);
      setFlowData(null);

      try {
        // Check if feature details need to be fetched
        const needsFeatureDetails = !selectedFeature;

        // Fetch both APIs in parallel if needed
        const promises: Promise<any>[] = [triggerFlow({ featureRequestId: featureRequestId }).unwrap()];

        if (needsFeatureDetails) {
          promises.push(triggerFeatureDetails({ featureRequestId: featureRequestId }).unwrap());
        }

        const results = await Promise.all(promises);

        // Set flow data
        setFlowData(results[0]);

        // Set feature details if fetched
        if (needsFeatureDetails && results[1]) {
          setSelectedFeature(results[1]);
        }
      } catch (error) {
        console.error("Load data error:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [featureRequestId, triggerFlow, triggerFeatureDetails]);

  useEffect(() => {
    if (featureRequestId && !hasLoggedRef.current && selectedFeature) {
      const customProps = {
        FeatureRequestId: selectedFeature.featureRequestId,
        FeatureTitle: selectedFeature.title,
        Product: selectedFeature.productName,
        Source: selectedFeature.source,
        CreatedDate: selectedFeature.createdDate ? new Date(selectedFeature.createdDate).toISOString() : ""
      };
      logPageView("Feature Request Flow Page", window.location.href, customProps);
      hasLoggedRef.current = true;
    }
  }, [featureRequestId, selectedFeature]);

  if (!isLoading && isError) {
    return (
      <div className="mainCard" style={styles.featureFlowCard}>
        <div style={styles.featureFlowHeader}>
          <CustomBreadcrumb {...bcrops} key={key} />
          <div style={styles.betaBadge}>
            <New16Regular fontSize={12} style={{ marginRight: 4 }} />
            <span>Beta</span>
          </div>
        </div>
        <DisplayAPIsException error={error} height={350} />
      </div>
    );
  }

  return (
    <div className="mainCard" style={styles.featureFlowCard}>
      <div style={styles.featureFlowHeader}>
        <CustomBreadcrumb {...bcrops} key={key} />
        <div style={styles.betaBadge}>
          <New16Regular fontSize={12} style={{ marginRight: 4 }} />
          <span>Beta</span>
        </div>
      </div>
      <div
        style={{
          width: "100%",
          padding: "0px 0px 20px 0px",
          position: "relative",
          minHeight: 300
        }}
      >
        <div style={{ padding: "1em 24px 0 24px" }}>
          <Text variant="xxLarge" styles={{ root: { fontWeight: 600, color: "rgb(0, 120, 212)", marginLeft: 20 } }}>
            Where is my Feature Request
          </Text>
        </div>

        {/* Data validation warning banner */}
        <div style={{ margin: "16px 24px 0 24px" }}>
          <WarningBanner message="Where is my FR page? - This data is still being validated and should not be used yet." />
        </div>

        {/* Error placeholder */}
        {flowData?.error && (
          <p style={{ color: "red", textAlign: "center" }}>Unable to fetch flow. Check FeatureRequestId.</p>
        )}

        {/* Flow container */}
        <div
          style={{
            marginTop: 20,
            padding: 0,
            borderRadius: 10,
            minHeight: 300,
            position: "relative",
            width: "100%"
          }}
        >
          {/* Always show Feature Details once selected (regardless of stages) */}
          {!loading && selectedFeature && (
            <FeatureDetailsCard
              featureId={selectedFeature.featureRequestId}
              title={selectedFeature.title}
              description={selectedFeature.description || "Description text goes here"}
              product={selectedFeature.productName}
              source={selectedFeature.source}
              owner={selectedFeature.createdBy}
              createdDate={
                selectedFeature.createdDate ? new Date(selectedFeature.createdDate).toLocaleDateString("en-US") : ""
              }
              url={selectedFeature.url}
            />
          )}

          {/* Stage flow - show if stages exist */}
          {!loading && flowData?.stages?.length > 0 && (
            <div className="fade-in" style={{ margin: "0 auto 8px auto", width: "100%", position: "relative" }}>
              <StageFlow
                stages={flowData.stages.map((stage: any, idx: number) => ({
                  id: stage.id,
                  label: stage.label,
                  color: stage.color || "#0078D4",
                  order: typeof stage.order === "number" ? stage.order : idx,
                  days: stage.daysInStatus,
                  startDate: stage.startDate,
                  endDate: stage.endDate
                }))}
                transitions={
                  flowData.transitions?.map((t: any) => ({
                    ...t,
                    color: t.color || "#333"
                  })) || []
                }
              />
            </div>
          )}

          {/* Message when no stages available */}
          {!loading && flowData && (!flowData.stages || flowData.stages.length === 0) && (
            <div
              className="fade-in"
              style={{
                margin: "40px auto",
                padding: "32px",
                textAlign: "center",
                background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                borderRadius: "12px",
                border: "2px dashed #dee2e6",
                maxWidth: "600px"
              }}
            >
              <div
                style={{
                  width: "64px",
                  height: "64px",
                  margin: "0 auto 16px",
                  background: "linear-gradient(135deg, #0078D4 0%, #106ebe 100%)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0.3
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <Text
                variant="xLarge"
                styles={{
                  root: {
                    fontWeight: 600,
                    color: "#495057",
                    display: "block",
                    marginBottom: "12px"
                  }
                }}
              >
                No Stage Information Available
              </Text>
              <Text
                variant="medium"
                styles={{
                  root: {
                    color: "#6c757d",
                    lineHeight: "1.6"
                  }
                }}
              >
                Stage flow data is not available for this feature request at the moment. Please check back later or
                contact the feature owner for more information.
              </Text>
            </div>
          )}

          {/* Overlay spinner while loading */}
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "rgba(255,255,255,0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 10,
                zIndex: 10
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  border: "4px solid #0078D4",
                  borderTop: "4px solid transparent",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Spinner & fade-in animation style */}
      <style>
        {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease-in;
                    }

                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}
      </style>
    </div>
  );
};

export default FeatureFlowPage;
